import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const PAGE_SIZE = 10;

const transformReferral = (ref) => ({
  _id: ref.workerId || ref._id,
  name: ref.name,
  phone: ref.phone,
  onBoardingDate: ref.onBoardingDate || ref.createdAt || null,
  shift: ref.shift ?? 0,
  earning: ref.earning ?? 0,
  referralStatus: ref.referralStatus ?? null,
  referralBonusReceived: ref.referralBonusReceived ?? false,
});

const extractReferralsFromResponse = (response) => {
  if (Array.isArray(response)) {
    return { referrals: response.map(transformReferral), totalReferrals: null, totalEarning: null };
  }

  if (response && response.success === true && response.data) {
    const list =
      (Array.isArray(response.data.referredWorkers) && response.data.referredWorkers) ||
      (Array.isArray(response.data.referrals) && response.data.referrals) ||
      (Array.isArray(response.data) && response.data) ||
      [];

    return {
      referrals: list.map(transformReferral),
      totalReferrals: response.data.totalReferrals ?? null,
      totalEarning: response.data.totalEarning ?? null,
    };
  }

  return { referrals: [], totalReferrals: null, totalEarning: null };
};

const getEndpointPath = (workerType, userId) => {
  if (workerType === "admin" || workerType === "superadmin") {
    return `/lenskart/list-referred-admin-workers/${userId}`;
  }
  return `/lenskart/list-referred-workers/${userId}`;
};

export const useReferredWorkers = ({ token, userId, workerType, month, year, name }) => {
  const [allReferrals, setAllReferrals] = useState([]);
  const [visibleReferrals, setVisibleReferrals] = useState([]);
  const [backendTotalReferrals, setBackendTotalReferrals] = useState(null);
  const [backendTotalEarning, setBackendTotalEarning] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!userId) {
        setError("User not found");
        setInitialLoading(false);
        return;
      }

      try {
        setInitialLoading(true);
        setError(null);

        const params = {};
        if (month) params.month = month;
        if (year) params.year = year;
        if (name) params.name = name;

        const response = await axios.get(
          `${BASE_URL}${getEndpointPath(workerType, userId)}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: Object.keys(params).length ? params : undefined,
          }
        );

        if (cancelled) return;

        const raw = response.data;
        const { referrals, totalReferrals, totalEarning } =
          extractReferralsFromResponse(raw);

        setAllReferrals(referrals);
        setVisibleReferrals(referrals.slice(0, PAGE_SIZE));
        setPage(1);
        setBackendTotalReferrals(totalReferrals);
        setBackendTotalEarning(totalEarning);
        setHasMore(referrals.length > PAGE_SIZE);
      } catch (err) {
        if (cancelled) return;
        console.error("Error loading referrals:", err);
        setError(err.message || "Failed to load referrals.");
      } finally {
        if (!cancelled) setInitialLoading(false);
      }
    };
    load();

    return () => {
      cancelled = true;
    };
  }, [userId, token, workerType, month, year, name]);

  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);

    setPage((prev) => {
      const nextPage = prev + 1;
      const referralsArray = Array.isArray(allReferrals) ? allReferrals : [];
      const nextSlice = referralsArray.slice(0, nextPage * PAGE_SIZE);
      setVisibleReferrals(nextSlice);
      setHasMore(nextSlice.length < referralsArray.length);
      return nextPage;
    });
    setLoadingMore(false);
  }, [loadingMore, hasMore, allReferrals]);

  return {
    allReferrals,
    visibleReferrals,
    totalReferrals: backendTotalReferrals,
    totalEarning: backendTotalEarning,
    hasMore,
    loadMore,
    initialLoading,
    loadingMore,
    error,
  };
};